import AddIcon from "@mui/icons-material/Add";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import { PostgrestClient } from "@supabase/postgrest-js";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useAsync, useDebounce } from "react-use";
import { useIdentity } from "../../containers/IdentityContainer";
import { useSnackbar } from "../../containers/SnackbarContainer";
import { usePermissions } from "../../hooks/usePermissions";
import { useTokenMaker } from "../../hooks";
import { Aircraft } from "../../types";
import { Permission } from "../../utils/permissions";
import { getPostgrestUrl } from "../../utils";
import { ConfirmationDialog } from "../../widgets/ConfirmationDialog";
import { Loading } from "../../widgets/Loading";
import { LabelingToolPresenter } from "./LabelingToolPresenter";

const postgrest = new PostgrestClient(getPostgrestUrl());

type Props = {};

export const LabelingTool: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { aircraftId } = useParams();
  const [confirmDeploy, setConfirmDeploy] = React.useState<boolean>(false);
  const { url: webURL } = useTokenMaker();
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  useIdentity(); // Keep for auth
  const { hasPermission } = usePermissions();

  // const [aircrafts, setAircrafts] = React.useState<Aircraft[]>([]);
  const [limitToCompleted, setLimitToCompleted] =
    React.useState<boolean>(false);
  const [completedAircraft, setCompletedAircraft] = React.useState<{
    [key: string]: boolean;
  }>({});
  const [limitToNotCompleted, setLimitToNotCompleted] =
    React.useState<boolean>(false);
  const [selectedAircraft, setSelectedAircraft] = React.useState<Aircraft>();
  const [img, setImg] = React.useState(new Image());
  const [imgSide, setImgSide] = React.useState(new Image());

  const { loading, value: selectableAircrafts } = useAsync(async () => {
    const { data } = await postgrest
      .rpc("aircraft_raw", {
        anything: "anything",
      })
      .select("id, make, model, locked")
      .order("make")
      .order("model");
    const aircrafts = data as Aircraft[];
    const defaultAircraft =
      aircrafts.find((a) => a.id === aircraftId) || aircrafts[0];
    setSelectedAircraft(await fetchAircraft(defaultAircraft.id));
    fetchCompletedAircraft(aircrafts);
    return aircrafts;
  }, []);

  const fetchCompletedAircraft = async (aircrafts: Aircraft[]) => {
    const completedAircraft = {};
    const { data: completedAircraftResults } = await postgrest
      .rpc("is_aircraft_complete", {
        aircraft_ids: aircrafts.map((a) => a.id),
      })
      .select();

    for (const aircraft of completedAircraftResults) {
      completedAircraft[aircraft.aircraft_id] = aircraft.is_complete;
    }
    setCompletedAircraft(completedAircraft);
  };

  // fetch image from the server. use loaded/setLoaded in the even that we're
  // changing airplanes
  async function fetchImage(imageURL: string, setIt: (x: any) => void) {
    let newImage = new Image();
    newImage.setAttribute("crossOrigin", "");
    newImage.crossOrigin = "Anonymous";
    newImage.src = imageURL.replace("public", "");
    newImage.onload = () => {
      setIt(newImage);
    };
  }

  const gisIntersection = async (geom1, geom2) => {
    postgrest.schema = "web";
    return postgrest.rpc("gis_intersection", {
      geom1,
      geom2,
    });
  };

  React.useEffect(() => {
    if (window.location.host.indexOf("staging.airplx.com") > -1) {
      snackbar.notify({
        text: t('screens.labelingTool.snackbar.staging'),
        severity: "warning",
      });
    }
  }, [t]);

  const fetchAircraft = async (id: string): Promise<Aircraft> => {
    const { data: aircrafts } = await postgrest
      .rpc("aircraft_raw", {
        anything: "anything",
      })
      .select()
      .eq("id", id);
    return aircrafts[0] as Aircraft;
  };

  // Debounce upsert_aircraft calls to prevent API flooding on rapid typing
  useDebounce(
    async () => {
      if (!selectedAircraft) {
        return;
      }
      const { id, geomz_text: _geomz_text, ...data } = selectedAircraft;
      await postgrest.rpc("upsert_aircraft", {
        an_aircraft: {
          id,
          ...data,
        },
      });
      await fetchCompletedAircraft(selectableAircrafts);
    },
    500,
    [selectedAircraft]
  );

  React.useEffect(() => {
    if (!selectedAircraft) {
      return;
    }
    if (selectedAircraft.image) {
      fetchImage(selectedAircraft.image, setImg);
    }
    if (selectedAircraft.side_image) {
      fetchImage(selectedAircraft.side_image, setImgSide);
    }
  }, [selectedAircraft]);

  if (loading || !selectedAircraft || !img) {
    return <Loading />;
  }

  const isComplete = (a: Aircraft): boolean => {
    if (!a) {
      return false;
    }
    if (a.linked_to) {
      return true;
    }
    return completedAircraft[a.id] ?? false;
  };

  const getEmojiForAircraft = (aircraft: Aircraft): string => {
    if (aircraft.locked) {
      return "🔒";
    }

    if (isComplete(aircraft)) {
      return "✅";
    }
    return "";
  };

  const isPeter = hasPermission(Permission.ACCESS_SUPER_ADMIN);

  return (
    <Box sx={{ p: 1, overflow: "scroll", height: "100%" }}>
      <Stack direction="row" spacing={1}>
        <Autocomplete
          disablePortal
          sx={{ width: 500 }}
          options={selectableAircrafts
            .filter((a) => (limitToCompleted ? isComplete(a) : true))
            .filter((a) => (limitToNotCompleted ? !isComplete(a) : true))}
          getOptionLabel={(option: Aircraft) =>
            `${getEmojiForAircraft(option)} ${option.make} ${option.model}`
          }
          onChange={async (event: any, newValue: Aircraft | null) => {
            if (!newValue?.id) {
              return;
            }
            const aircraft = await fetchAircraft(newValue.id);
            setSelectedAircraft(aircraft);
            if (aircraft) {
              window.location.hash = "#/labeling-tool/" + aircraft.id;
            }
            if (isComplete(aircraft)) {
              snackbar.notify({
                text: t('screens.labelingTool.snackbar.editingCompleted'),
                severity: "warning",
              });
            }
          }}
          renderInput={(params) => (
            <TextField {...params} label={t('screens.labelingTool.labels.aircraft')} size="small" />
          )}
          value={selectedAircraft}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={limitToCompleted}
              onChange={() => {
                setLimitToNotCompleted(false);
                setLimitToCompleted(!limitToCompleted);
              }}
            />
          }
          label={t('screens.labelingTool.labels.limitToCompleted')}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={limitToNotCompleted}
              onChange={() => {
                setLimitToCompleted(false);
                setLimitToNotCompleted(!limitToNotCompleted);
              }}
            />
          }
          label={t('screens.labelingTool.labels.limitToNotCompleted')}
        />
        <Button
          color="success"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={async () => {
            const { data: new_aircraft_id } = await postgrest.rpc(
              "new_aircraft",
              {
                anything: "hi",
              }
            );
            navigate(`/labeling-tool/${new_aircraft_id}`);
          }}
        >
          {t('screens.labelingTool.buttons.new')}
        </Button>
        {isPeter && (
          <>
            <FormControlLabel
              control={<Switch checked={selectedAircraft.locked} />}
              onChange={async (evt: React.ChangeEvent<HTMLInputElement>) => {
                const confirmed = confirm(t('screens.labelingTool.confirmations.lockToggle'));
                if (!confirmed) {
                  return;
                }
                setSelectedAircraft({
                  ...selectedAircraft,
                  locked: evt.target.checked,
                });
                await postgrest.rpc("toggle_locked", {
                  aircraft_id: selectedAircraft.id,
                  new_value: evt.target.checked,
                });
              }}
              label={t('screens.labelingTool.labels.locked')}
            />
            <FormControlLabel
              control={<Switch checked={selectedAircraft.is_hidden} />}
              onChange={async (evt: React.ChangeEvent<HTMLInputElement>) => {
                const confirmed = confirm(t('screens.labelingTool.confirmations.hiddenToggle'));
                if (!confirmed) {
                  return;
                }
                setSelectedAircraft({
                  ...selectedAircraft,
                  is_hidden: evt.target.checked,
                });
                await postgrest.rpc("toggle_is_hidden", {
                  aircraft_id: selectedAircraft.id,
                  new_value: evt.target.checked,
                });
              }}
              label={t('screens.labelingTool.labels.isHidden')}
            />
            <Button
              onClick={() => setConfirmDeploy(true)}
              variant="contained"
              color="primary"
            >
              {t('screens.labelingTool.buttons.deploy')}
            </Button>
            {confirmDeploy && (
              <ConfirmationDialog
                title={t('screens.labelingTool.confirmations.deployTitle')}
                text={t('screens.labelingTool.confirmations.deployText')}
                onCancel={() => setConfirmDeploy(false)}
                onClose={() => setConfirmDeploy(false)}
                onOk={async () => {
                  try {
                    const response = await fetch(
                      `${webURL}/jobs/update-aircraft`
                    );

                    if (!response.ok) {
                      throw new Error(t('screens.labelingTool.errors.deployFailed'));
                    }

                    snackbar.notify({
                      text: t('screens.labelingTool.snackbar.deploymentStarted'),
                      severity: "success",
                    });
                  } catch (err: unknown) {
                    const errorMsg =
                      err instanceof Error ? err.message : t('screens.labelingTool.snackbar.deploymentFailed');
                    snackbar.notify({
                      text: errorMsg,
                      severity: "error",
                    });
                  } finally {
                    setConfirmDeploy(false);
                  }
                }}
                okText={t('screens.labelingTool.confirmations.deployOk')}
              />
            )}
          </>
        )}
      </Stack>
      {selectedAircraft.locked && !isPeter ? (
        <LabelingToolPresenter
          img={img}
          imgSide={imgSide}
          aircrafts={selectableAircrafts}
          aircraft={selectedAircraft}
          setAircraft={() => {
            // noop
          }}
          gisIntersection={gisIntersection}
        />
      ) : (
        <LabelingToolPresenter
          img={img}
          imgSide={imgSide}
          aircrafts={selectableAircrafts}
          aircraft={selectedAircraft}
          setAircraft={setSelectedAircraft}
          gisIntersection={gisIntersection}
        />
      )}
    </Box>
  );
};
